// v210 admin API client for Sigstore (cosign) trust-policy
// management. Backs the AdminSigstorePolicies page. Roadmap item 21
// ("Sigstore / cosign-Integration native"; item 26 in the
// recommended delivery order).
//
// The verifier these policies feed (`internal/sigstore.Verifier`) is
// invoked from format-adapter upload paths and the upstream verify-
// on-pull gate; this client is purely the operator surface for
// authoring the rows.

import useSWR from 'swr';

export interface SigstorePolicy {
  id: string;
  name: string;
  repo_id?: string | null;
  identity_pattern: string;
  issuer_pattern: string;
  fulcio_root_ca: string;
  rekor_url: string;
  require_rekor: boolean;
  enabled: boolean;
  created_at: string;
  created_by?: string;
}

export interface SigstorePolicyInput {
  name: string;
  repo_id?: string | null;
  identity_pattern: string;
  issuer_pattern: string;
  fulcio_root_ca?: string;
  rekor_url?: string;
  require_rekor?: boolean;
  enabled?: boolean;
}

interface ListResponse {
  policies: SigstorePolicy[];
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

// useSigstorePolicies lists every policy. Pass `'global'` to filter
// to the policies whose `repo_id IS NULL`, or a UUID to filter to a
// specific repo's overrides; omit to get the full set.
export function useSigstorePolicies(scope?: string) {
  const url = scope
    ? `/api/admin/sigstore/policies?repo_id=${encodeURIComponent(scope)}`
    : '/api/admin/sigstore/policies';
  return useSWR<ListResponse>(url, json);
}

export async function createSigstorePolicy(
  input: SigstorePolicyInput,
): Promise<{ id: string; created_at: string }> {
  const res = await fetch('/api/admin/sigstore/policies', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

export async function updateSigstorePolicy(
  id: string,
  input: SigstorePolicyInput,
): Promise<void> {
  const res = await fetch(
    `/api/admin/sigstore/policies/${encodeURIComponent(id)}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function deleteSigstorePolicy(id: string): Promise<void> {
  const res = await fetch(
    `/api/admin/sigstore/policies/${encodeURIComponent(id)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!res.ok && res.status !== 204)
    throw new Error(`${res.status}: ${await res.text()}`);
}
